import React, { useState, useEffect, useCallback } from 'react';
import { carouselApiService, FeedCube, SlideRef } from '../services/carouselApiService';
import { Slide } from '../types';
import AuthenticatedImage from './AuthenticatedImage';

interface CubeDetailModalProps {
  cube: FeedCube;
  initialSlides: Slide[];
  onClose: () => void;
}

const CubeDetailModal: React.FC<CubeDetailModalProps> = ({ cube, initialSlides, onClose }) => {
  const [allSlides, setAllSlides] = useState<Slide[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  const parseSlideRefs = (refsJson: string): SlideRef[] => {
    try {
      return JSON.parse(refsJson) || [];
    } catch {
      return [];
    }
  };

  const hydrateAll = useCallback(async () => {
    const refs = parseSlideRefs(cube.slideRefs);
    const alreadyLoaded = new Set(initialSlides.map(s => s.id));
    const remaining = refs.filter(r => !alreadyLoaded.has(r.slideId));

    if (remaining.length === 0) {
      const slideMap = new Map(initialSlides.map(s => [s.id, s]));
      setAllSlides(refs.map(r => slideMap.get(r.slideId)).filter(Boolean) as Slide[]);
      setLoading(false);
      return;
    }

    let newSlides: Slide[] = [];
    try {
      newSlides = await carouselApiService.hydrateSlides(remaining);
    } catch (err) {
      console.error('CubeDetailModal: Failed to hydrate slides:', err);
    }

    const allHydrated = [...initialSlides, ...newSlides];
    const slideMap = new Map(allHydrated.map(s => [s.id, s]));
    setAllSlides(refs.map(r => slideMap.get(r.slideId)).filter(Boolean) as Slide[]);
    setLoading(false);
  }, [cube.slideRefs, initialSlides]);

  useEffect(() => {
    hydrateAll();
  }, [hydrateAll]);

  // Reset image index when changing slides
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [selectedIndex]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') setSelectedIndex(prev => Math.max(0, prev - 1));
      if (e.key === 'ArrowRight') setSelectedIndex(prev => Math.min(allSlides.length - 1, prev + 1));
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [allSlides.length, onClose]);

  const currentSlide = allSlides[selectedIndex];
  const sortedImages = currentSlide?.images?.length > 0
    ? [...currentSlide.images].sort((a, b) => a.displayOrder - b.displayOrder)
    : [];
  const hasMultipleImages = sortedImages.length > 1;
  const currentImage = sortedImages[currentImageIndex] || sortedImages[0];

  const arrowButtonStyle: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'rgba(255, 255, 255, 0.85)',
    border: 'none',
    borderRadius: '50%',
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: '20px',
    color: 'var(--text-primary)',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.3)',
    zIndex: 2,
    padding: 0,
    lineHeight: 1,
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--bg-primary)',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '700px',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: '1px solid var(--border-light)',
          flexShrink: 0,
        }}>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1.25rem',
              color: 'var(--text-primary)',
              padding: '4px',
              lineHeight: 1,
            }}
          >&#10005;</button>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>{cube.title}</div>
            {cube.reason && (
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '2px' }}>{cube.reason}</div>
            )}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', minWidth: '50px', textAlign: 'right' }}>
            {allSlides.length > 0 ? `${selectedIndex + 1} of ${allSlides.length}` : ''}
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {loading && allSlides.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
              Loading slides...
            </div>
          ) : currentSlide ? (
            <div>
              {/* Image area with per-image carousel */}
              <div style={{ padding: '16px 16px 0', backgroundColor: 'var(--bg-primary)' }}>
                <div style={{
                  position: 'relative',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  backgroundColor: 'var(--bg-secondary)',
                }}>
                  {sortedImages.length > 0 ? (
                    sortedImages.map((img, index) => (
                      <AuthenticatedImage
                        key={img.id}
                        shopId={currentSlide.shopId}
                        slideId={currentSlide.id}
                        imageId={img.id}
                        alt={currentSlide.name}
                        loading="eager"
                        size="thumb_800"
                        style={{
                          width: '100%',
                          aspectRatio: '4/3',
                          objectFit: 'cover',
                          pointerEvents: 'none',
                          ...(index !== currentImageIndex ? { position: 'absolute', visibility: 'hidden' } : {})
                        }}
                      />
                    ))
                  ) : (
                    <div style={{ aspectRatio: '4/3', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}>
                      No Image
                    </div>
                  )}

                  {/* Per-image arrows */}
                  {hasMultipleImages && currentImageIndex > 0 && (
                    <button
                      onClick={() => setCurrentImageIndex(prev => prev - 1)}
                      style={{ ...arrowButtonStyle, left: '8px' }}
                      aria-label="Previous image"
                    >&#8249;</button>
                  )}
                  {hasMultipleImages && currentImageIndex < sortedImages.length - 1 && (
                    <button
                      onClick={() => setCurrentImageIndex(prev => prev + 1)}
                      style={{ ...arrowButtonStyle, right: '8px' }}
                      aria-label="Next image"
                    >&#8250;</button>
                  )}

                  {/* Image dot indicators */}
                  {hasMultipleImages && (
                    <div style={{
                      position: 'absolute',
                      bottom: '10px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      backgroundColor: 'rgba(0, 0, 0, 0.4)',
                      zIndex: 1,
                    }}>
                      {sortedImages.map((_, i) => (
                        <div
                          key={i}
                          onClick={() => setCurrentImageIndex(i)}
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: i === currentImageIndex ? 'var(--bg-card)' : 'rgba(255, 255, 255, 0.4)',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease',
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Slide-to-slide navigation */}
              {allSlides.length > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                }}>
                  <button
                    onClick={() => setSelectedIndex(prev => Math.max(0, prev - 1))}
                    disabled={selectedIndex === 0}
                    style={{
                      backgroundColor: selectedIndex === 0 ? 'var(--bg-secondary)' : 'var(--primary)',
                      color: selectedIndex === 0 ? 'var(--border-medium)' : 'var(--text-inverse)',
                      border: 'none',
                      borderRadius: '20px',
                      padding: '6px 16px',
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      cursor: selectedIndex === 0 ? 'default' : 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >&#8249; Prev Slide</button>
                  <button
                    onClick={() => setSelectedIndex(prev => Math.min(allSlides.length - 1, prev + 1))}
                    disabled={selectedIndex === allSlides.length - 1}
                    style={{
                      backgroundColor: selectedIndex === allSlides.length - 1 ? 'var(--bg-secondary)' : 'var(--primary)',
                      color: selectedIndex === allSlides.length - 1 ? 'var(--border-medium)' : 'var(--text-inverse)',
                      border: 'none',
                      borderRadius: '20px',
                      padding: '6px 16px',
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      cursor: selectedIndex === allSlides.length - 1 ? 'default' : 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >Next Slide &#8250;</button>
                </div>
              )}

              {/* Slide info */}
              <div style={{ padding: '4px 20px 20px' }}>
                <h3 style={{ margin: '0 0 12px', fontSize: '1.125rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                  {currentSlide.name}
                </h3>
                {currentSlide.description && (
                  <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                    {currentSlide.description}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
              No slides available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CubeDetailModal;
